import { useCallback } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useGTMConsentState } from '@b-stock/fe-core/bstock-react'

import { FeatureFlags } from '@/helpers/featureFlags'
import type { GaSessionIdPayload } from '@/helpers/telemetry/gaSessionIds'
import { getGaSessionIdPayload } from '@/helpers/telemetry/gaSessionIds'

/**
 * `analytics_storage`, as a plain boolean, without ever throwing.
 *
 * `useGTMConsentState()` throws when there is no `GTMConsentProvider` above it.
 * That provider is mounted app-wide today, but a telemetry nicety must not be
 * able to turn a bid or offer into a crash — "never block a submission" is the
 * whole posture of this feature. So a missing provider degrades to "no
 * consent" (capture nothing), which is also the fail-safe direction.
 *
 * Wrapping the call in try/catch is safe with respect to the rules of hooks:
 * `useGTMConsentState` calls `useContext` unconditionally and only then throws,
 * so the hook order is identical on every render whether or not we catch.
 */
const useAnalyticsConsentGranted = (): boolean => {
  try {
    // Strictly `true`: `null` means GTM has not synced consent yet, and that
    // is not permission.
    // oxlint-disable-next-line react-hooks/rules-of-hooks
    return useGTMConsentState().analytics_storage === true
  } catch {
    return false
  }
}

/**
 * GLOB-4954 / GLOB-5129: the single decision point for GA-id forwarding. Two
 * independent gates, both of which must open:
 *
 * - the LaunchDarkly kill switch, and
 * - the visitor's live analytics consent.
 *
 * Every bid / offer submission path reads both through this hook rather than
 * calling `getGaSessionIdPayload()` directly, so the behavior is decided in
 * exactly one place:
 *
 * - flag ON + consent granted → the getter returns the GA ids when the GA
 *              cookies are present (and still `{}` when they are absent or GA
 *              is blocked, exactly as `getGaSessionIdPayload()` already
 *              behaves).
 * - flag OFF → the getter always returns `{}`, so payloads are byte-identical
 *              to the pre-GLOB-4954 behavior.
 * - flag MISSING / LaunchDarkly unavailable → `useFlag` falls back to the
 *              flag's `defaultValue`, which is `false`; same as OFF.
 * - consent not granted → `{}` regardless of the flag. The flag can only ever
 *              subtract; it cannot resurrect capture for a visitor who has
 *              declined or withdrawn.
 *
 * ## Why Google Consent Mode and not a CMP-specific check (GLOB-5129)
 *
 * 3MP application code is deliberately decoupled from the cookie-consent
 * vendor. A custom GTM template synchronises the vendor's choices outward as
 * Google Consent Mode state, and app code gates on THAT abstraction — so the
 * CMP can be swapped without touching a line of this repo. Reading a
 * vendor-specific global here (an OneTrust `window` property, say) would put
 * the vendor's name back into application code and defeat that.
 *
 * `useGTMConsentState()` is fe-core's canonical reader for that state, already
 * used by `PendoTrackingScript` and `Pendo`. Its provider is mounted
 * unconditionally as the outermost provider in fe-core's `BStockAppProviders`,
 * which both routers render, so it is available on every bid / offer path.
 *
 * ## Timing and fail-safety
 *
 * Consent Mode state arrives asynchronously, after GTM boots: fe-core models
 * "not yet known" as `null`, distinct from a definite `false`. Only an
 * explicit `true` counts as consent here, so unknown / not-yet-synced / denied
 * all suppress capture. We would rather drop a legitimate measurement than
 * capture an identifier the visitor has not affirmatively allowed.
 *
 * Returns a *getter* rather than the payload itself so the cookies are read at
 * submit time (they can change between render and submit), and so nothing on
 * the submit path has to await anything — `useFlag` is a synchronous read of
 * the already-bootstrapped LD flag set, and the consent state is a context
 * value. Because consent lives in context, a withdrawal re-renders every
 * consumer and the next submission is gated immediately, without waiting for
 * the (deliberately undeleted) GA cookies to expire.
 */
export const useGaSessionIdPayload = (): (() => GaSessionIdPayload) => {
  const enabled = useFlag(FeatureFlags.ENABLE_GA_OFFLINE_EVENT_CAPTURE)
  const analyticsConsentGranted = useAnalyticsConsentGranted()

  return useCallback(
    () => (enabled ? getGaSessionIdPayload(analyticsConsentGranted) : {}),
    [enabled, analyticsConsentGranted]
  )
}
